import { Suspense } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { blogPosts } from '../../mocks/blogPosts';
import { useSEO, generateBreadcrumbSchema } from '../../utils/seo';

export default function BlogDetailPage() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  // Call useSEO hook before any conditional returns
  useSEO({
    title: post ? `${post.title} | PURECODE Ltd Warrington Fintech Blog` : 'Blog Post Not Found | PURECODE Ltd',
    description: post?.excerpt || 'Blog post not found',
    keywords: post?.tags.join(', ') || '',
    schema: post ? {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: post.date,
          author: {
            '@type': 'Organization',
            name: post.author
          },
          publisher: {
            '@type': 'Organization',
            name: 'PURECODE Ltd',
            logo: {
              '@type': 'ImageObject',
              url: 'https://purecode.uk/logo.png'
            }
          },
          articleSection: post.category,
          keywords: post.tags.join(', ')
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: post.title, url: `/blog/${post.slug}` }
        ])
      ]
    } : undefined
  });

  // Now safe to do conditional return after hooks are called
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && (p.category === post.category || p.tags.some(tag => post.tags.includes(tag))))
    .slice(0, 3);

  return (
    <Suspense fallback={<div className="min-h-screen bg-white"></div>}>
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Breadcrumb */}
        <nav className="pt-32 pb-8 px-6 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-teal-600 transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-900">{post.title}</span>
            </div>
          </div>
        </nav>

        {/* Article Header */}
        <article className="pb-20">
          <header className="px-6 pb-12 bg-slate-50">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <span className="px-4 py-2 bg-teal-500 text-white rounded-full font-semibold text-sm">
                  {post.category}
                </span>
                <span className="text-gray-600">{post.date}</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-600">{post.readTime}</span>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                {post.title}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                {post.excerpt}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-team-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{post.author}</div>
                  <div className="text-sm text-gray-600">Expert Team</div>
                </div>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="px-6 -mt-6 mb-12">
            <div className="max-w-5xl mx-auto">
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="px-6">
            <div className="max-w-3xl mx-auto">
              <div className="prose prose-lg max-w-none">
                <div className="text-gray-700 leading-relaxed space-y-6 text-base">
                  {post.content.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-6">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Tags:</h4>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map(tag => (
                    <span
                      key={tag}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-teal-50 hover:text-teal-600 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Share this article:</h4>
                <div className="flex gap-3">
                  <button className="w-10 h-10 bg-gray-100 hover:bg-teal-500 text-gray-700 hover:text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 bg-gray-100 hover:bg-teal-500 text-gray-700 hover:text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-twitter-x-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 bg-gray-100 hover:bg-teal-500 text-gray-700 hover:text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-lg"></i>
                  </button>
                  <button className="w-10 h-10 bg-gray-100 hover:bg-teal-500 text-gray-700 hover:text-white rounded-full flex items-center justify-center transition-colors cursor-pointer">
                    <i className="ri-mail-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-20 px-6 bg-slate-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map(relatedPost => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3 text-sm text-gray-500">
                        <span className="px-3 py-1 bg-teal-50 text-teal-600 rounded-full font-medium text-xs">
                          {relatedPost.category}
                        </span>
                        <span>{relatedPost.date}</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-20 px-6 bg-gradient-to-br from-teal-500 to-teal-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Transform Your Financial Services?
            </h2>
            <p className="text-xl text-teal-50 mb-8">
              Let's discuss how our AI-driven solutions can help your business thrive
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-white text-teal-600 rounded-full font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              Get in Touch
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
